import React, { Suspense } from "react";
import Snackbar from "@mui/material/Snackbar";
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import WatchLaterIcon from '@mui/icons-material/WatchLater';
import ReplayIcon from '@mui/icons-material/Replay';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
//import logo from './logo.svg';
import "./App.css";
import { BrowserRouter as Router, Redirect, Route, Switch } from "react-router-dom";
import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider } from "@mui/material/styles";

import store from "./app/store";
import { Provider } from "react-redux";
import "./i18n";
import "typeface-roboto";
import axios from "./components/Axios";
import PrivateRoute from "./components/PrivateRoute";
import ErrorBoundary from "./components/ErrorBoundary";

import Page404 from "./pages/Page404";
import Login from "./pages/Login";
import ForgotPW from "./pages/ForgotPW";
import PWReset from "./pages/PWReset";
import RequestCode from "./pages/RequestCode";
import LogoTest2 from "./pages/LogoTest2";
import LogoCOCO from "./pages/LogoCOCO";
import LogoCoco2 from "./pages/LogoCoco2";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import Logout from "./pages/Logout";
import Register from "./pages/Register";

import Upload from "./pages/Upload";
import Search from "./pages/Search";
import Ask from "./pages/Ask";
import Videos from "./pages/Videos";
import VideoTabs from "./pages/VideoTabs";
import VideoProgress from "./pages/VideoProgress";
import Account from "./pages/Account";
import Invoice from "./pages/Invoice";
import Pricing from "./pages/Pricing";

import {formatFileSize} from "./app/utils";
import { lightTheme, darkTheme } from "./app/theme";

class Index extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      items: [],
      error: false,
      errorMessage: "",
      api_url: axios.defaults.baseURL,
      uploadQueue: [],
      uploading: null,
      uploaded: [],
      successMessage: "",
      dark: false
    };

    this.onDrop = this.onDrop.bind(this);
    this.removeFromQueue = this.removeFromQueue.bind(this);
  }

  componentDidMount() {
    this.onResize();
    window.addEventListener('resize', this.onResize);
    this.onAxiosError = (e) => this.setState({ error: true, errorMessage: e.detail });
    this.onAppSuccess = (e) => this.setState({ successMessage: e.detail });
    window.addEventListener('axios-error', this.onAxiosError);
    window.addEventListener('app-success', this.onAppSuccess);
    this.onThemeChange = (e) => this.setState({ dark: e.detail });
    window.addEventListener('theme-change', this.onThemeChange);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('axios-error', this.onAxiosError);
    window.removeEventListener('app-success', this.onAppSuccess);
    window.removeEventListener('theme-change', this.onThemeChange);
  }

  onResize() {
    if (window.innerWidth < 1024) {
      document.body.style.transform = 'scale(calc(' + window.innerWidth + '/1024))';
      //document.body.style.minHeight = Math.floor(window.innerHeight * 1024 / window.innerWidth) + 'px';
    } else
      document.body.style.transform = '';
  }

  changeAPI() {
    let v = window.prompt('API Url:', process.env.REACT_APP_API_BASE);
    if (v === null) return;

    localStorage.setItem("api_url", v);
    window.location.replace("/");
  }

  onDrop(files) {
    this.setState((prev) => {
      const wasIdle = !prev.uploading && prev.uploadQueue.length === 0;
      const queue = [...prev.uploadQueue, ...files];
      if (wasIdle) setTimeout(() => this.uploadNext(), 0);
      return { uploadQueue: queue };
    });
  }

  removeFromQueue(file) {
    this.setState((prev) => ({ uploadQueue: prev.uploadQueue.filter((f) => f !== file) }));
  }

  uploadNext() {
    let file = null;
    this.setState((prev) => {
      if (prev.uploading || prev.uploadQueue.length === 0) return null;
      file = prev.uploadQueue[0];
      return { uploadQueue: prev.uploadQueue.slice(1), uploading: { file, progress: 0 } };
    }, () => {
      if (!file) return;
      const dev_params = localStorage.getItem("dev_params") || '{"asr_version": "large-v3"}';
      let p = {};
      try { p = JSON.parse(dev_params); } catch {}

      const formData = new FormData();
      for (const key in p) formData.append(key, p[key]);
      formData.append("user_id", localStorage.getItem("user_id"));
      // the video's name without its file extension (kenzo.mp4 -> kenzo)
      formData.append("project_name", file.name.replace(/\.(mp4|m4v|mov|mkv|avi|webm|mpe?g|wmv|flv|mxf|ts|3gp)$/i, "") || file.name);
      formData.append("video_file", file);
      formData.append("folder", file.folder || "");
      //formData.append("language", "auto");
      formData.append("notify", false);
      formData.append("notify_type", "email");
      //formData.append("multi_language", false);
      formData.append("email", "");
      formData.append("sms", "");
      //formData.append("features", ["Actions", "Logos", "Nudity", "Speech"]);

      axios.post("/video_project", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          this.setState((prev) => ({ uploading: { ...prev.uploading, progress: e.loaded / e.total } }));
        },
      }).then(() => {
        window.dispatchEvent(new CustomEvent("upload-complete"));
        this.setState((prev) => ({
          uploading: null,
          uploaded: [{ file, status: "done" }, ...prev.uploaded],
        }), () => this.uploadNext());
      }).catch((e) => {
        this.setState((prev) => ({
          uploading: null,
          uploaded: [{ file, status: "error", message: "Error: " + e?.response?.data?.message }, ...prev.uploaded],
        }), () => this.uploadNext());
      });
    });
  }

  render() {
    return (
      <React.StrictMode>
        <Provider store={store}>
          <ThemeProvider theme={this.state.dark ? darkTheme : lightTheme}>

          <Suspense fallback="loading">
            <Router>
              <CssBaseline />
              {/* a page that crashes shows a message instead of blanking the app; going to another page clears it */}
              <Route render={({ location }) => (
              <ErrorBoundary resetKey={location.pathname}>
              <Switch>
                <PrivateRoute path="/" exact render={(props) => <Videos {...props} onDrop={this.onDrop} />} />
                <Route path="/terms" component={Terms} />
                <Route path="/privacy" component={Privacy} />
                <Route path="/pricing" component={Pricing} />
                <Route path="/login" component={Login} />
                <Route path="/forgotpw" component={ForgotPW} />
                <Route path="/pwreset" component={PWReset} />
                <Route path="/request_code" component={RequestCode} />
                <Route path="/register" component={Register} />
                <Route path="/logout" component={Logout} />

                {/* pages of a logged-in user: others go to the login page first, and come back after */}
                <PrivateRoute path="/upload" component={Upload} />
                <PrivateRoute path="/search" component={Search} />
                <PrivateRoute path="/ask/:chat_id?" component={Ask} />
                <Redirect from="/chat" to="/ask" />
                <PrivateRoute path="/project/:project_id/:tab" component={VideoTabs} />
                <PrivateRoute path="/project_progress/:project_id" component={VideoProgress} />
                <PrivateRoute path="/account/:tab" component={Account} />

                <PrivateRoute path="/invoice/:id" component={Invoice} />

                <Route path="/logo-test2" component={LogoTest2} />
                <Route path="/logo-coco-test" component={LogoCOCO} />
                <Route path="/logo2" component={LogoCoco2} />

                <Route path="*" component={Page404}></Route>
              </Switch>
              </ErrorBoundary>
              )} />
              <Snackbar
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                open={!!(this.state.uploading || this.state.uploadQueue.length > 0 || this.state.uploaded.length > 0)}
              >
                <Alert severity="info" variant="filled" icon={false}>
                  {this.state.uploadQueue.map((file, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                      <WatchLaterIcon fontSize="small" sx={{ color: "white" }} />
                      <div>
                        <div style={{ fontSize: "8px", lineHeight: "0px" }}>{file.folder}</div>
                        <div>{file.name} <span style={{ fontSize: "8px" }}>{formatFileSize(file.size)}</span></div>
                      </div>
                      <div style={{ flex: 1 }}></div>
                      <IconButton size="small" onClick={() => this.removeFromQueue(file)}>
                        <DeleteIcon fontSize="small" sx={{ color: "white" }} />
                      </IconButton>
                    </div>
                  ))}
                  {this.state.uploading && (
                    <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                      <CircularProgress variant="determinate" value={Math.round(this.state.uploading.progress * 100)} size={20} sx={{ color: "white" }} />
                      <div>
                        <div style={{ fontSize: "8px", lineHeight: "0px" }}>{this.state.uploading.file.folder}</div>
                        <div>{this.state.uploading.file.name} <span style={{ fontSize: "8px" }}>{formatFileSize(this.state.uploading.file.size)}</span></div>
                      </div>
                      <div style={{ flex: 1 }}></div>
                      <div>{Math.round(this.state.uploading.progress * 100)}%</div>
                    </div>
                  )}
                  {this.state.uploaded.map((entry, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                      {entry.status === "error"
                        ? <ErrorIcon fontSize="small" sx={{ color: "#ffcdd2" }} />
                        : <CheckCircleIcon fontSize="small" sx={{ color: "white" }} />
                      }
                      <div>
                        <div style={{ fontSize: "8px", lineHeight: "0px" }}>{entry.file.folder}</div>
                        <div>{entry.file.name} <span style={{ fontSize: "8px" }}>{formatFileSize(entry.file.size)}</span></div>
                      </div>
                      <div style={{ flex: 1 }}></div>
                      {entry.status === "error" && <div style={{ color: "#ffcdd2" }}>{entry.message || "Error"}</div>}
                      {entry.status === "error" && (
                        <IconButton size="small" onClick={() => {
                          this.setState((prev) => ({ uploaded: prev.uploaded.filter((e) => e !== entry) }));
                          this.onDrop([entry.file]);
                        }}>
                          <ReplayIcon fontSize="small" sx={{ color: "white" }} />
                        </IconButton>
                      )}
                      <IconButton size="small" onClick={() => this.setState((prev) => ({ uploaded: prev.uploaded.filter((_, j) => j !== i) }))}>
                        <CloseIcon fontSize="small" sx={{ color: "white" }} />
                      </IconButton>
                    </div>
                  ))}
                </Alert>
              </Snackbar>
              <Snackbar
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
                open={this.state.error}
                autoHideDuration={6000}
                onClose={() => this.setState({ error: false })}
              >
                <Alert variant="filled" severity="error" onClose={() => this.setState({ error: false })}>
                  {this.state.errorMessage}
                </Alert>
              </Snackbar>
              <Snackbar 
                 anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
              open={this.state.successMessage !== ""} autoHideDuration={6000} onClose={() => this.setState({ successMessage: "" })}>
                <Alert
                  onClose={() => this.setState({ successMessage: "" })}
                  severity="success"
                  variant="filled"
                  sx={{ width: '100%' }}
                >
                  {this.state.successMessage}
                </Alert>
              </Snackbar>
              {process.env.REACT_APP_ENV === 'dev' &&
                <Route path="/login">
                  <div className="api-url">
                    <b>API: </b>
                    {this.state.api_url}
                    <button className="link-button" onClick={this.changeAPI} style={{marginLeft: "6px", fontSize: "8px"}}>&#x270E;</button>
                  </div>
                </Route>
              }
            </Router>
          </Suspense>

          </ThemeProvider>
        </Provider>
      </React.StrictMode>
    );
  }
}

export default Index;
